import {
  findMindmapNode,
  findMindmapParent,
  MINDMAP_BRANCH_COLOURS,
  MINDMAP_ROOT_COLOUR,
  newMindmapNode,
  nowIso,
  STATUS_COLOURS,
  type ItemIcon,
  type ItemStatus,
  type MindmapNode,
  type NodeCore,
} from '@blueprint/domain'
import { ICON_GLYPHS, type IconGlyph } from '../../components/ItemFields.js'

/**
 * The mindmap canvas, ported from the legacy Electron renderer (~/Projects/mindmap/
 * renderer.js): layout, drawing, hit-testing, pan and zoom, drag to move or re-parent,
 * undo/redo and the minimap. Everything that is a DOM control — menus, the inline text
 * editor, the note dialog, the colour picker, confirmations — is React's; the engine asks
 * for them through callbacks and is told the outcome through its methods.
 */

const NODE = {
  rootPadX: 24,
  rootPadY: 14,
  padX: 16,
  padY: 10,
  rootFontSize: 18,
  fontSize: 14,
  rootRadius: 16,
  radius: 10,
  hGap: 60,
  vGap: 10,
  collapseButtonRadius: 8,
  checkboxSize: 14,
  checkboxGap: 8,
  statusIconSize: 14,
  iconSize: 16,
  noteIconSize: 11,
}

/** The marker on a node with a note: a page with a folded corner. Not one of the icons. */
const NOTE_GLYPH: IconGlyph = { stroke: 'M6 3h8l4 4v14H6ZM14 3v4h4M9 12h6M9 16h6', colour: '#f1c40f' }

/** Opacity of cards hidden by the status filter. */
const FILTER_FADE = 0.15
/** Pixels before a click on a node becomes a drag. */
const DRAG_THRESHOLD = 6
const UNDO_DEPTH = 50
const MIN_ZOOM = 0.15
const MAX_ZOOM = 4
const MINIMAP_W = 200
const MINIMAP_H = 150

/** A node with the layout the last pass gave it. */
interface LNode extends Omit<MindmapNode, 'children'> {
  children: LNode[]
  x: number
  y: number
  width: number
  height: number
  totalHeight: number
  depth: number
}

interface Camera {
  x: number
  y: number
  zoom: number
}

interface Theme {
  bg: string
  text: string
  muted: string
  surface: string
  accent: string
  font: string
}

export interface EngineCallbacks {
  /** Every committed edit, with the whole tree. Not called in read-only mode. */
  onCommit: (root: MindmapNode) => void
  onSelect: (id: string | null) => void
  /** Right-click: a node, or the canvas (null, meaning the root). */
  onContextMenu: (id: string | null, x: number, y: number) => void
  /** Start editing a node's text in place (double-click, F2, a new node). */
  onEditText: (id: string) => void
  onDeleteRequest: (id: string) => void
  /** The linked-cards badge on a node was clicked. */
  onLinks: (id: string, x: number, y: number) => void
  onView: (zoom: number) => void
  onToast: (message: string) => void
  onHistory: (canUndo: boolean, canRedo: boolean) => void
}

function toLNode(node: MindmapNode, depth = 0): LNode {
  return {
    ...node,
    children: node.children.map((child) => toLNode(child, depth + 1)),
    x: 0,
    y: 0,
    width: 0,
    height: 0,
    totalHeight: 0,
    depth,
  }
}

/** The node without its layout: every stored field, so none is lost on the way out. */
function toNode(node: LNode): MindmapNode {
  const { x: _x, y: _y, width: _width, height: _height, totalHeight: _totalHeight, depth: _depth, children, ...fields } = node
  return { ...fields, children: children.map(toNode) }
}

function countDescendants(node: { children: { children: unknown[] }[] }): number {
  let count = node.children.length
  for (const child of node.children) count += countDescendants(child as typeof node)
  return count
}

export function shiftColour(hex: string, amount: number): string {
  const channel = (start: number) => Math.max(0, Math.min(255, parseInt(hex.slice(start, start + 2), 16) + amount))
  return `#${[1, 3, 5].map((start) => channel(start).toString(16).padStart(2, '0')).join('')}`
}

function readTheme(element: Element): Theme {
  const style = getComputedStyle(element)
  const token = (name: string, fallback: string) => style.getPropertyValue(name).trim() || fallback
  return {
    bg: token('--bg', '#1a1a2e'),
    text: token('--text', '#e0e0f0'),
    muted: token('--muted', '#8888aa'),
    surface: token('--surface', '#16213e'),
    accent: token('--accent', MINDMAP_ROOT_COLOUR),
    font: getComputedStyle(document.body).fontFamily || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  }
}

export class MindmapEngine {
  private root: LNode | null = null
  private ctx: CanvasRenderingContext2D
  private readonly minimapCtx: CanvasRenderingContext2D | null
  private width = 0
  private height = 0
  private cam: Camera = { x: 0, y: 0, zoom: 1 }
  private theme: Theme
  private readOnly = false
  private filter: ItemStatus | '' = ''
  private linkCounts = new Map<string, number>()
  private layout = new Map<string, LNode>()

  private selectedId: string | null = null
  private hoveredId: string | null = null
  private undoStack: string[] = []
  private redoStack: string[] = []

  private panning = false
  private panStart = { x: 0, y: 0 }
  private camStart = { x: 0, y: 0 }
  private drag = {
    pending: false,
    active: false,
    node: null as LNode | null,
    startX: 0,
    startY: 0,
    dropTargetId: null as string | null,
    origOffsetX: 0,
    origOffsetY: 0,
    before: '',
  }

  private readonly listeners: [EventTarget, string, EventListener, AddEventListenerOptions?][] = []
  private frame = 0
  private readonly resizeObserver: ResizeObserver | null

  constructor(
    private readonly canvas: HTMLCanvasElement,
    private readonly minimap: HTMLCanvasElement | null,
    private readonly callbacks: EngineCallbacks,
  ) {
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Canvas is not available.')
    this.ctx = ctx
    this.minimapCtx = minimap?.getContext('2d') ?? null
    this.theme = readTheme(canvas)

    this.listen(canvas, 'mousedown', (event) => this.onMouseDown(event as MouseEvent))
    this.listen(canvas, 'mousemove', (event) => this.onMouseMove(event as MouseEvent))
    this.listen(canvas, 'mouseup', () => this.onMouseUp())
    this.listen(canvas, 'mouseleave', () => this.onMouseLeave())
    this.listen(canvas, 'dblclick', (event) => this.onDoubleClick(event as MouseEvent))
    this.listen(canvas, 'wheel', (event) => this.onWheel(event as WheelEvent), { passive: false })
    this.listen(canvas, 'contextmenu', (event) => this.onContextMenu(event as MouseEvent))
    if (minimap) this.listen(minimap, 'mousedown', (event) => this.onMinimapClick(event as MouseEvent))

    this.resizeObserver = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(() => this.resize())
    this.resizeObserver?.observe(canvas.parentElement ?? canvas)
    this.resize()
  }

  private listen(target: EventTarget, type: string, listener: EventListener, options?: AddEventListenerOptions): void {
    target.addEventListener(type, listener, options)
    this.listeners.push([target, type, listener, options])
  }

  destroy(): void {
    for (const [target, type, listener, options] of this.listeners) target.removeEventListener(type, listener, options)
    this.resizeObserver?.disconnect()
    cancelAnimationFrame(this.frame)
  }

  // --- state in ------------------------------------------------------------------

  /** Replaces the tree. A new map resets the view and history; a reload keeps the view. */
  load(root: MindmapNode, options: { reset: boolean }): void {
    this.root = toLNode(root)
    if (options.reset) {
      this.undoStack = []
      this.redoStack = []
      this.selectedId = this.root.id
      this.cam = { x: 0, y: 0, zoom: 1 }
      this.callbacks.onView(1)
      this.callbacks.onSelect(this.selectedId)
    } else if (this.selectedId && !findMindmapNode(root, this.selectedId)) {
      this.select(this.root.id)
    }
    this.emitHistory()
    this.render()
  }

  setReadOnly(readOnly: boolean): void {
    this.readOnly = readOnly
  }

  setFilter(filter: ItemStatus | ''): void {
    this.filter = filter
    this.render()
  }

  setLinkCounts(counts: Map<string, number>): void {
    this.linkCounts = counts
    this.render()
  }

  /** Re-reads the colour tokens, after the app theme changes. */
  refreshTheme(): void {
    this.theme = readTheme(this.canvas)
    this.render()
  }

  // --- state out -----------------------------------------------------------------

  getRoot(): MindmapNode | null {
    return this.root ? toNode(this.root) : null
  }

  getSelectedId(): string | null {
    return this.selectedId
  }

  getNode(id: string): MindmapNode | null {
    const node = this.root ? this.find(id) : null
    return node ? toNode(node) : null
  }

  isRoot(id: string): boolean {
    return this.root?.id === id
  }

  /** Where a node is on screen, relative to the canvas, for overlays. */
  nodeScreenRect(id: string): { x: number; y: number; width: number; height: number; fontSize: number } | null {
    const node = this.layout.get(id)
    if (!node) return null
    const s = this.worldToScreen(node.x, node.y)
    return {
      x: s.x,
      y: s.y,
      width: node.width * this.cam.zoom,
      height: node.height * this.cam.zoom,
      fontSize: (node.depth === 0 ? NODE.rootFontSize : NODE.fontSize) * this.cam.zoom,
    }
  }

  // --- edits ---------------------------------------------------------------------

  select(id: string | null): void {
    this.selectedId = id
    this.callbacks.onSelect(id)
    this.render()
  }

  addChild(parentId?: string): void {
    if (!this.root || this.readOnly) return
    const parent = this.find(parentId ?? this.selectedId ?? this.root.id) ?? this.root
    this.snapshot()
    const colour =
      parent.id === this.root.id
        ? MINDMAP_BRANCH_COLOURS[this.root.children.length % MINDMAP_BRANCH_COLOURS.length] ?? MINDMAP_ROOT_COLOUR
        : this.colourOf(parent)
    const child = toLNode(newMindmapNode('New Topic', colour), parent.depth + 1)
    parent.children.push(child)
    parent.collapsed = false
    this.select(child.id)
    this.commit()
    // After the layout pass has placed it, so the editor lands on the node.
    requestAnimationFrame(() => this.callbacks.onEditText(child.id))
  }

  addSibling(id?: string): void {
    if (!this.root) return
    const node = this.find(id ?? this.selectedId ?? '')
    if (!node || node.id === this.root.id) return
    const parent = this.parentOf(node.id)
    if (parent) this.addChild(parent.id)
  }

  deleteNode(id: string): void {
    if (!this.root || this.readOnly || id === this.root.id) return
    const parent = this.parentOf(id)
    if (!parent) return
    this.snapshot()
    parent.children = parent.children.filter((child) => child.id !== id)
    // A relationship to a deleted node goes with it.
    const live = new Set<string>()
    const collect = (node: LNode) => {
      live.add(node.id)
      node.children.forEach(collect)
    }
    collect(this.root)
    const prune = (node: LNode) => {
      if (node.relationships.some((link) => !live.has(link.targetId))) node.relationships = node.relationships.filter((link) => live.has(link.targetId))
      node.children.forEach(prune)
    }
    prune(this.root)
    this.select(parent.id)
    this.commit()
  }

  setTitle(id: string, title: string): void {
    const trimmed = title.trim()
    if (trimmed) this.setFields(id, { title: trimmed })
  }

  setDescription(id: string, description: string): void {
    this.setFields(id, { description })
  }

  setStatus(id: string, status: ItemStatus | null): void {
    this.setFields(id, { status })
  }

  setIcon(id: string, icon: ItemIcon | null): void {
    this.setFields(id, { icon })
  }

  /** Any of the fields a node shares with cards, as one undoable edit; nothing when none changes. */
  setFields(id: string, patch: Partial<Omit<NodeCore, 'id' | 'createdAt' | 'updatedAt'>>): void {
    const node = this.find(id)
    if (!node || this.readOnly) return
    const changed = (Object.keys(patch) as (keyof typeof patch)[]).filter((key) => JSON.stringify(node[key]) !== JSON.stringify(patch[key]))
    if (changed.length === 0) return
    this.snapshot()
    Object.assign(node, patch, { updatedAt: nowIso() })
    this.commit()
  }

  /** Recolours the node and all of its descendants. */
  setColour(id: string, colour: string): void {
    const node = this.find(id)
    if (!node || !this.root || this.readOnly) return
    this.snapshot()
    this.propagateColour(node, colour)
    this.commit()
  }

  toggleCollapse(id?: string): void {
    const node = this.find(id ?? this.selectedId ?? '')
    if (!node || node.children.length === 0) return
    this.snapshot()
    node.collapsed = !node.collapsed
    this.commit()
  }

  collapseAllChildren(id?: string): void {
    const node = this.find(id ?? this.root?.id ?? '')
    if (!node) return
    this.snapshot()
    const walk = (current: LNode) => {
      for (const child of current.children) {
        child.collapsed = true
        walk(child)
      }
    }
    walk(node)
    this.commit()
  }

  expandAll(): void {
    if (!this.root) return
    this.snapshot()
    const walk = (node: LNode) => {
      node.collapsed = false
      node.children.forEach(walk)
    }
    walk(this.root)
    this.commit()
  }

  undo(): void {
    const previous = this.undoStack.pop()
    if (!previous || !this.root || this.readOnly) return
    this.redoStack.push(JSON.stringify(toNode(this.root)))
    this.root = toLNode(JSON.parse(previous) as MindmapNode)
    this.commit()
  }

  redo(): void {
    const next = this.redoStack.pop()
    if (!next || !this.root || this.readOnly) return
    this.undoStack.push(JSON.stringify(toNode(this.root)))
    this.root = toLNode(JSON.parse(next) as MindmapNode)
    this.commit()
  }

  // --- view ----------------------------------------------------------------------

  zoomIn(): void {
    this.setZoom(this.cam.zoom * 1.2)
  }

  zoomOut(): void {
    this.setZoom(this.cam.zoom / 1.2)
  }

  resetView(): void {
    this.cam = { x: 0, y: 0, zoom: 1 }
    this.callbacks.onView(1)
    this.render()
  }

  /** Centres the map and zooms so every shown node fills the view, within the zoom limits. */
  fitView(): void {
    if (!this.root) return
    this.doLayout()
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (const node of this.layout.values()) {
      minX = Math.min(minX, node.x)
      minY = Math.min(minY, node.y)
      maxX = Math.max(maxX, node.x + node.width + NODE.collapseButtonRadius * 2)
      maxY = Math.max(maxY, node.y + node.height)
    }
    if (minX === Infinity) return
    const pad = 40
    const zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, this.width / (maxX - minX + pad * 2), this.height / (maxY - minY + pad * 2)))
    this.cam = { x: -(minX + maxX) / 2, y: -(minY + maxY) / 2, zoom }
    this.callbacks.onView(zoom)
    this.render()
  }

  /** Brings a node to the middle of the canvas and selects it, expanding its ancestors. */
  focusNode(id: string): void {
    if (!this.root) return
    const path: LNode[] = []
    const walk = (node: LNode): boolean => {
      path.push(node)
      if (node.id === id) return true
      for (const child of node.children) if (walk(child)) return true
      path.pop()
      return false
    }
    if (!walk(this.root)) return
    for (const ancestor of path.slice(0, -1)) ancestor.collapsed = false
    this.doLayout()
    const node = this.layout.get(id)
    if (node) this.cam = { ...this.cam, x: -(node.x + node.width / 2), y: -(node.y + node.height / 2) }
    this.select(id)
  }

  private setZoom(zoom: number): void {
    this.cam.zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, zoom))
    this.callbacks.onView(this.cam.zoom)
    this.render()
  }

  /** The keyboard shortcuts of the legacy app. Returns whether the key was used. */
  handleKey(event: KeyboardEvent): boolean {
    if (!this.root) return false
    const node = this.selectedId ? this.find(this.selectedId) : null
    const isRoot = node?.id === this.root.id
    const mod = event.metaKey || event.ctrlKey
    switch (event.key) {
      case 'Tab':
        if (node && !this.readOnly) this.addChild(node.id)
        return true
      case 'Enter':
        if (node && !isRoot && !this.readOnly) this.addSibling(node.id)
        return true
      case 'Delete':
      case 'Backspace':
        if (!node || isRoot || this.readOnly) return false
        this.callbacks.onDeleteRequest(node.id)
        return true
      case ' ':
        if (!node || node.children.length === 0) return false
        this.toggleCollapse(node.id)
        return true
      case 'F2':
        if (!node || this.readOnly) return false
        this.callbacks.onEditText(node.id)
        return true
      case 'ArrowLeft': {
        const parent = node ? this.parentOf(node.id) : null
        if (parent) this.select(parent.id)
        return true
      }
      case 'ArrowRight':
        if (node && node.children.length > 0 && !node.collapsed) this.select(node.children[0]!.id)
        else if (node?.collapsed) this.toggleCollapse(node.id)
        return true
      case 'ArrowUp':
      case 'ArrowDown':
        if (node) this.navigateSibling(node, event.key === 'ArrowUp' ? -1 : 1)
        return true
      case 'z':
      case 'Z':
        if (!mod) return false
        if (event.shiftKey) this.redo()
        else this.undo()
        return true
      case 'y':
        if (!mod) return false
        this.redo()
        return true
      case '=':
      case '+':
        if (!mod) return false
        this.zoomIn()
        return true
      case '-':
        if (!mod) return false
        this.zoomOut()
        return true
      case '0':
        if (!mod) return false
        this.resetView()
        return true
      default:
        return false
    }
  }

  /** The whole map, expanded and unfiltered, as a PNG at twice the drawn size. */
  async toPng(): Promise<Blob | null> {
    if (!this.root) return null
    const saved = { root: this.root, ctx: this.ctx, width: this.width, height: this.height, cam: this.cam, filter: this.filter, selected: this.selectedId, hovered: this.hoveredId }
    const expanded = toLNode(toNode(this.root))
    const expand = (node: LNode) => {
      node.collapsed = false
      node.children.forEach(expand)
    }
    expand(expanded)
    this.root = expanded
    this.filter = ''
    this.selectedId = null
    this.hoveredId = null
    this.doLayout()
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (const node of this.layout.values()) {
      minX = Math.min(minX, node.x)
      minY = Math.min(minY, node.y)
      maxX = Math.max(maxX, node.x + node.width + NODE.collapseButtonRadius * 2)
      maxY = Math.max(maxY, node.y + node.height)
    }
    const pad = 40
    const scale = 2
    const width = Math.ceil(maxX - minX + pad * 2)
    const height = Math.ceil(maxY - minY + pad * 2)
    const canvas = document.createElement('canvas')
    canvas.width = Math.min(width * scale, 16_000)
    canvas.height = Math.min(height * scale, 16_000)
    const ctx = canvas.getContext('2d')
    if (!ctx) return null
    const fit = Math.min(scale, canvas.width / width, canvas.height / height)
    ctx.setTransform(fit, 0, 0, fit, 0, 0)
    this.ctx = ctx
    this.width = width
    this.height = height
    this.cam = { x: -(minX + maxX) / 2, y: -(minY + maxY) / 2, zoom: 1 }
    try {
      this.paint(false)
    } finally {
      Object.assign(this, { root: saved.root, ctx: saved.ctx, width: saved.width, height: saved.height, cam: saved.cam, filter: saved.filter, selectedId: saved.selected, hoveredId: saved.hovered })
      this.render()
    }
    return new Promise((resolve) => canvas.toBlob(resolve, 'image/png'))
  }

  // --- internals -----------------------------------------------------------------

  private find(id: string): LNode | null {
    return this.root ? (findMindmapNode(this.root as unknown as MindmapNode, id) as unknown as LNode | null) : null
  }

  private parentOf(id: string): LNode | null {
    return this.root ? (findMindmapParent(this.root as unknown as MindmapNode, id) as unknown as LNode | null) : null
  }

  private colourOf(node: LNode): string {
    if (node.colour) return node.colour
    let current = this.parentOf(node.id)
    while (current) {
      if (current.colour) return current.colour
      current = this.parentOf(current.id)
    }
    return MINDMAP_ROOT_COLOUR
  }

  private propagateColour(node: LNode, colour: string): void {
    node.colour = colour
    node.updatedAt = nowIso()
    for (const child of node.children) this.propagateColour(child, colour)
  }

  private navigateSibling(node: LNode, direction: number): void {
    const parent = this.parentOf(node.id)
    if (!parent) return
    const next = parent.children[parent.children.indexOf(node) + direction]
    if (next) this.select(next.id)
  }

  private snapshot(state?: string): void {
    if (!this.root) return
    this.undoStack.push(state ?? JSON.stringify(toNode(this.root)))
    if (this.undoStack.length > UNDO_DEPTH) this.undoStack.shift()
    this.redoStack = []
  }

  private commit(): void {
    this.render()
    this.emitHistory()
    if (this.root && !this.readOnly) this.callbacks.onCommit(toNode(this.root))
  }

  private emitHistory(): void {
    this.callbacks.onHistory(this.undoStack.length > 0, this.redoStack.length > 0)
  }

  private resize(): void {
    const host = this.canvas.parentElement ?? this.canvas
    const rect = host.getBoundingClientRect()
    this.width = Math.max(1, Math.floor(rect.width))
    this.height = Math.max(1, Math.floor(rect.height))
    const dpr = window.devicePixelRatio || 1
    this.canvas.width = this.width * dpr
    this.canvas.height = this.height * dpr
    this.canvas.style.width = `${this.width}px`
    this.canvas.style.height = `${this.height}px`
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0)
    if (this.minimap && this.minimapCtx) {
      this.minimap.width = MINIMAP_W * dpr
      this.minimap.height = MINIMAP_H * dpr
      this.minimapCtx.setTransform(dpr, 0, 0, dpr, 0, 0)
    }
    this.render()
  }

  /** Coalesced to one paint per frame. */
  render(): void {
    if (this.frame) return
    this.frame = requestAnimationFrame(() => {
      this.frame = 0
      this.paint(true)
    })
  }

  private font(depth: number, size: number): string {
    return `${depth === 0 ? 'bold ' : ''}${size}px ${this.theme.font}`
  }

  private leftAreaWidth(node: LNode): number {
    let width = NODE.checkboxSize + NODE.checkboxGap
    if (node.status) width += NODE.statusIconSize + NODE.checkboxGap
    if (node.icon) width += NODE.iconSize + NODE.checkboxGap
    return width
  }

  private measure(node: LNode, depth: number): void {
    const isRoot = depth === 0
    const fontSize = isRoot ? NODE.rootFontSize : NODE.fontSize
    this.ctx.font = this.font(depth, fontSize)
    node.width = this.ctx.measureText(node.title).width + (isRoot ? NODE.rootPadX : NODE.padX) * 2 + this.leftAreaWidth(node)
    node.height = fontSize + (isRoot ? NODE.rootPadY : NODE.padY) * 2
    node.depth = depth
    if (node.collapsed || node.children.length === 0) {
      node.totalHeight = node.height
      return
    }
    let total = 0
    for (const child of node.children) {
      this.measure(child, depth + 1)
      total += child.totalHeight
    }
    total += (node.children.length - 1) * NODE.vGap
    node.totalHeight = Math.max(node.height, total)
  }

  private place(node: LNode, x: number, y: number): void {
    node.x = x + node.offsetX
    node.y = y + node.offsetY
    this.layout.set(node.id, node)
    if (node.collapsed || node.children.length === 0) return
    const childX = node.x + node.width + NODE.hGap
    let total = 0
    for (const child of node.children) total += child.totalHeight
    total += (node.children.length - 1) * NODE.vGap
    let childY = node.y + node.height / 2 - total / 2
    for (const child of node.children) {
      this.place(child, childX, childY + child.totalHeight / 2 - child.height / 2)
      childY += child.totalHeight + NODE.vGap
    }
  }

  private doLayout(): void {
    if (!this.root) return
    this.layout.clear()
    this.measure(this.root, 0)
    this.place(this.root, -this.root.width / 2, -this.root.height / 2)
  }

  private worldToScreen(wx: number, wy: number): { x: number; y: number } {
    return {
      x: (wx + this.cam.x) * this.cam.zoom + this.width / 2,
      y: (wy + this.cam.y) * this.cam.zoom + this.height / 2,
    }
  }

  private screenToWorld(sx: number, sy: number): { x: number; y: number } {
    return {
      x: (sx - this.width / 2) / this.cam.zoom - this.cam.x,
      y: (sy - this.height / 2) / this.cam.zoom - this.cam.y,
    }
  }

  private matchesFilter(node: LNode): boolean {
    return !this.filter || node.status === this.filter
  }

  private paint(withMinimap: boolean): void {
    const { ctx } = this
    if (!this.root) {
      ctx.clearRect(0, 0, this.width, this.height)
      return
    }
    this.doLayout()
    ctx.clearRect(0, 0, this.width, this.height)
    ctx.fillStyle = this.theme.bg
    ctx.fillRect(0, 0, this.width, this.height)
    this.drawGrid()
    this.drawNode(this.root, 0)
    if (withMinimap) this.drawMinimap()
  }

  private drawGrid(): void {
    const { ctx, cam } = this
    const size = 40 * cam.zoom
    if (size < 10) return
    const offsetX = (((cam.x * cam.zoom + this.width / 2) % size) + size) % size
    const offsetY = (((cam.y * cam.zoom + this.height / 2) % size) + size) % size
    ctx.save()
    ctx.strokeStyle = this.theme.muted
    ctx.globalAlpha = 0.08
    ctx.lineWidth = 1
    for (let x = offsetX; x < this.width; x += size) {
      ctx.beginPath()
      ctx.moveTo(x, 0)
      ctx.lineTo(x, this.height)
      ctx.stroke()
    }
    for (let y = offsetY; y < this.height; y += size) {
      ctx.beginPath()
      ctx.moveTo(0, y)
      ctx.lineTo(this.width, y)
      ctx.stroke()
    }
    ctx.restore()
  }

  /** Facing edge midpoints of two rects: left/right when further apart across, else top/bottom. */
  private connector(a: Rect, b: Rect, colour: string, lineWidth: number): void {
    const gapX = Math.max(b.x - (a.x + a.w), a.x - (b.x + b.w))
    const gapY = Math.max(b.y - (a.y + a.h), a.y - (b.y + b.h))
    const midX = (r: Rect) => r.x + r.w / 2
    const midY = (r: Rect) => r.y + r.h / 2
    let p1: Anchor
    let p2: Anchor
    if (gapX >= gapY) {
      const right = midX(b) >= midX(a)
      p1 = { x: right ? a.x + a.w : a.x, y: midY(a), dx: right ? 1 : -1, dy: 0 }
      p2 = { x: right ? b.x : b.x + b.w, y: midY(b), dx: right ? -1 : 1, dy: 0 }
    } else {
      const below = midY(b) >= midY(a)
      p1 = { x: midX(a), y: below ? a.y + a.h : a.y, dx: 0, dy: below ? 1 : -1 }
      p2 = { x: midX(b), y: below ? b.y : b.y + b.h, dx: 0, dy: below ? -1 : 1 }
    }
    const pull = (p: Anchor) => Math.max(10 * this.cam.zoom, (p.dx ? Math.abs(p2.x - p1.x) : Math.abs(p2.y - p1.y)) * 0.5)
    const k1 = pull(p1)
    const k2 = pull(p2)
    const { ctx } = this
    ctx.beginPath()
    ctx.moveTo(p1.x, p1.y)
    ctx.bezierCurveTo(p1.x + p1.dx * k1, p1.y + p1.dy * k1, p2.x + p2.dx * k2, p2.y + p2.dy * k2, p2.x, p2.y)
    ctx.strokeStyle = colour
    ctx.lineWidth = lineWidth
    ctx.lineCap = 'round'
    ctx.stroke()
  }

  private drawStatusIcon(status: ItemStatus, cx: number, cy: number, size: number): void {
    const { ctx } = this
    const colour = STATUS_COLOURS[status]
    const r = size / 2
    ctx.save()
    ctx.lineWidth = 1.75 * this.cam.zoom
    ctx.strokeStyle = colour
    ctx.fillStyle = colour
    if (status === 'done') {
      ctx.beginPath()
      ctx.arc(cx, cy, r, 0, Math.PI * 2)
      ctx.fill()
      ctx.beginPath()
      ctx.moveTo(cx - r * 0.45, cy + r * 0.02)
      ctx.lineTo(cx - r * 0.1, cy + r * 0.38)
      ctx.lineTo(cx + r * 0.5, cy - r * 0.35)
      ctx.strokeStyle = '#fff'
      ctx.stroke()
    } else {
      ctx.beginPath()
      ctx.arc(cx, cy, r - ctx.lineWidth / 2, 0, Math.PI * 2)
      ctx.stroke()
      if (status === 'in_progress') {
        ctx.beginPath()
        ctx.arc(cx, cy, r * 0.55, -Math.PI / 2, Math.PI / 2)
        ctx.closePath()
        ctx.fill()
      } else if (status === 'testing') {
        ctx.beginPath()
        ctx.arc(cx, cy, r * 0.3, 0, Math.PI * 2)
        ctx.fill()
      } else if (status === 'blocked') {
        // A bar across, like a no-entry sign.
        ctx.beginPath()
        ctx.moveTo(cx - r * 0.5, cy)
        ctx.lineTo(cx + r * 0.5, cy)
        ctx.stroke()
      }
    }
    ctx.restore()
  }

  /** A glyph centred on (cx, cy), `size` pixels square. */
  private drawGlyph(glyph: IconGlyph, cx: number, cy: number, size: number, fallback: string): void {
    const { ctx } = this
    const scale = size / 24
    ctx.save()
    ctx.translate(cx - size / 2, cy - size / 2)
    ctx.scale(scale, scale)
    ctx.lineWidth = 2
    ctx.lineCap = 'round'
    ctx.lineJoin = 'round'
    const colour = glyph.colour ?? fallback
    if (glyph.fill) {
      ctx.fillStyle = colour
      ctx.fill(new Path2D(glyph.fill))
      ctx.strokeStyle = '#fff'
    } else {
      ctx.strokeStyle = colour
    }
    ctx.stroke(new Path2D(glyph.stroke))
    ctx.restore()
  }

  private linkBadgeRect(node: LNode): Rect | null {
    const count = this.linkCounts.get(node.id)
    if (!count) return null
    const z = this.cam.zoom
    const s = this.worldToScreen(node.x, node.y)
    const w = (14 + String(count).length * 6) * z
    const h = 14 * z
    return { x: s.x + node.width * z - w + 4 * z, y: s.y + node.height * z - h / 2, w, h }
  }

  private drawNode(node: LNode, depth: number): void {
    const { ctx, cam } = this
    const isRoot = depth === 0
    const s = this.worldToScreen(node.x, node.y)
    const w = node.width * cam.zoom
    const h = node.height * cam.zoom

    if (s.x + w < -50 || s.x > this.width + 50 || s.y + h < -50 || s.y > this.height + 50) {
      if (!node.collapsed) for (const child of node.children) this.drawNode(child, depth + 1)
      return
    }

    const colour = this.colourOf(node)
    const selected = this.selectedId === node.id
    const hovered = this.hoveredId === node.id
    const dragged = this.drag.active && this.drag.node?.id === node.id
    const dropTarget = this.drag.active && this.drag.dropTargetId === node.id
    const radius = (isRoot ? NODE.rootRadius : NODE.radius) * cam.zoom
    const fontSize = (isRoot ? NODE.rootFontSize : NODE.fontSize) * cam.zoom

    if (dragged) {
      ctx.save()
      ctx.globalAlpha = 0.7
    }

    if (!node.collapsed) {
      for (const child of node.children) {
        const cs = this.worldToScreen(child.x, child.y)
        ctx.save()
        if (!this.matchesFilter(child)) ctx.globalAlpha *= FILTER_FADE
        this.connector(
          { x: s.x, y: s.y, w, h },
          { x: cs.x, y: cs.y, w: child.width * cam.zoom, h: child.height * cam.zoom },
          `${this.colourOf(child)}99`,
          Math.max(1.5, (isRoot ? 3.5 : 2.5) * cam.zoom),
        )
        ctx.restore()
      }
    }

    ctx.save()
    if (!this.matchesFilter(node)) ctx.globalAlpha *= FILTER_FADE

    ctx.save()
    ctx.shadowColor = selected ? `${colour}60` : 'rgba(0,0,0,0.18)'
    ctx.shadowBlur = (selected ? 20 : 8) * cam.zoom
    ctx.shadowOffsetY = 2 * cam.zoom
    ctx.beginPath()
    ctx.roundRect(s.x, s.y, w, h, radius)
    if (isRoot) {
      const gradient = ctx.createLinearGradient(s.x, s.y, s.x + w, s.y + h)
      gradient.addColorStop(0, colour)
      gradient.addColorStop(1, shiftColour(colour, -30))
      ctx.fillStyle = gradient
    } else {
      // An opaque base under the tint, so connectors do not show through the card.
      ctx.fillStyle = this.theme.surface
      ctx.fill()
      ctx.fillStyle = dropTarget ? `${colour}50` : selected ? `${colour}40` : hovered ? `${colour}28` : `${colour}18`
    }
    ctx.fill()
    ctx.restore()

    ctx.beginPath()
    ctx.roundRect(s.x, s.y, w, h, radius)
    if (dropTarget) {
      ctx.strokeStyle = this.theme.text
      ctx.lineWidth = 3 * cam.zoom
      ctx.setLineDash([6 * cam.zoom, 4 * cam.zoom])
    } else {
      ctx.strokeStyle = selected ? colour : `${colour}50`
      ctx.lineWidth = (selected ? 2.5 : 1.5) * cam.zoom
    }
    ctx.stroke()
    ctx.setLineDash([])

    if (node.description) {
      this.drawGlyph(NOTE_GLYPH, s.x + w - 9 * cam.zoom, s.y + 9 * cam.zoom, NODE.noteIconSize * cam.zoom, colour)
    }

    const done = node.status === 'done'
    const padX = (isRoot ? NODE.rootPadX : NODE.padX) * cam.zoom
    const cb = NODE.checkboxSize * cam.zoom
    const cbX = s.x + padX
    const cbY = s.y + h / 2 - cb / 2
    ctx.beginPath()
    ctx.roundRect(cbX, cbY, cb, cb, 3 * cam.zoom)
    ctx.fillStyle = done ? (isRoot ? '#fff' : colour) : 'rgba(255,255,255,0.08)'
    ctx.fill()
    ctx.strokeStyle = isRoot ? '#fff' : colour
    ctx.lineWidth = 1.5 * cam.zoom
    ctx.stroke()
    if (done) {
      ctx.beginPath()
      ctx.moveTo(cbX + cb * 0.22, cbY + cb * 0.52)
      ctx.lineTo(cbX + cb * 0.42, cbY + cb * 0.72)
      ctx.lineTo(cbX + cb * 0.78, cbY + cb * 0.3)
      ctx.strokeStyle = isRoot ? colour : '#fff'
      ctx.lineWidth = 2 * cam.zoom
      ctx.stroke()
    }

    if (node.status) {
      const size = NODE.statusIconSize * cam.zoom
      this.drawStatusIcon(node.status, cbX + cb + NODE.checkboxGap * cam.zoom + size / 2, s.y + h / 2, size)
    }

    if (node.icon) {
      const size = NODE.iconSize * cam.zoom
      const before = (NODE.checkboxSize + NODE.checkboxGap + (node.status ? NODE.statusIconSize + NODE.checkboxGap : 0)) * cam.zoom
      this.drawGlyph(ICON_GLYPHS[node.icon], cbX + before + size / 2, s.y + h / 2, size, isRoot ? '#fff' : colour)
    }

    const left = this.leftAreaWidth(node) * cam.zoom
    const textX = s.x + (w + left) / 2
    const textY = s.y + h / 2 + cam.zoom
    ctx.font = this.font(depth, fontSize)
    ctx.fillStyle = isRoot ? '#fff' : this.theme.text
    if (done && !isRoot) ctx.globalAlpha *= 0.55
    ctx.textAlign = 'center'
    ctx.textBaseline = 'middle'
    ctx.fillText(node.title, textX, textY)
    if (done) {
      const tw = ctx.measureText(node.title).width
      ctx.beginPath()
      ctx.moveTo(textX - tw / 2, textY)
      ctx.lineTo(textX + tw / 2, textY)
      ctx.strokeStyle = ctx.fillStyle
      ctx.lineWidth = 1.5 * cam.zoom
      ctx.stroke()
    }
    if (done && !isRoot) ctx.globalAlpha /= 0.55

    if (node.children.length > 0) {
      const bx = s.x + w + 2 * cam.zoom
      const by = s.y + h / 2
      const br = NODE.collapseButtonRadius * cam.zoom
      ctx.beginPath()
      ctx.arc(bx, by, br, 0, Math.PI * 2)
      ctx.fillStyle = node.collapsed ? `${colour}c0` : `${colour}80`
      ctx.fill()
      ctx.strokeStyle = `${colour}`
      ctx.lineWidth = cam.zoom
      ctx.stroke()
      if (node.collapsed) {
        ctx.font = `bold ${9 * cam.zoom}px ${this.theme.font}`
        ctx.fillStyle = '#fff'
        ctx.textAlign = 'center'
        ctx.textBaseline = 'middle'
        ctx.fillText(String(countDescendants(node)), bx, by)
      } else {
        ctx.beginPath()
        ctx.moveTo(bx - 3 * cam.zoom, by)
        ctx.lineTo(bx + 3 * cam.zoom, by)
        ctx.strokeStyle = '#fff'
        ctx.lineWidth = 1.5 * cam.zoom
        ctx.stroke()
      }
    }

    const badge = this.linkBadgeRect(node)
    if (badge) {
      ctx.beginPath()
      ctx.roundRect(badge.x, badge.y, badge.w, badge.h, badge.h / 2)
      ctx.fillStyle = this.theme.accent
      ctx.fill()
      ctx.font = `bold ${9 * cam.zoom}px ${this.theme.font}`
      ctx.fillStyle = '#fff'
      ctx.textAlign = 'center'
      ctx.textBaseline = 'middle'
      ctx.fillText(`▦ ${this.linkCounts.get(node.id)}`, badge.x + badge.w / 2, badge.y + badge.h / 2 + 0.5 * cam.zoom)
    }
    ctx.restore()

    if (!node.collapsed) for (const child of node.children) this.drawNode(child, depth + 1)
    if (dragged) ctx.restore()
  }

  private minimapTransform(): { scale: number; offsetX: number; offsetY: number } | null {
    let minX = Infinity
    let minY = Infinity
    let maxX = -Infinity
    let maxY = -Infinity
    for (const node of this.layout.values()) {
      minX = Math.min(minX, node.x)
      minY = Math.min(minY, node.y)
      maxX = Math.max(maxX, node.x + node.width)
      maxY = Math.max(maxY, node.y + node.height)
    }
    if (!Number.isFinite(minX)) return null
    const pad = 40
    minX -= pad
    minY -= pad
    maxX += pad
    maxY += pad
    const scale = Math.min(MINIMAP_W / (maxX - minX), MINIMAP_H / (maxY - minY))
    return {
      scale,
      offsetX: (MINIMAP_W - (maxX - minX) * scale) / 2 - minX * scale,
      offsetY: (MINIMAP_H - (maxY - minY) * scale) / 2 - minY * scale,
    }
  }

  private drawMinimap(): void {
    const ctx = this.minimapCtx
    if (!ctx) return
    ctx.clearRect(0, 0, MINIMAP_W, MINIMAP_H)
    ctx.fillStyle = this.theme.surface
    ctx.globalAlpha = 0.92
    ctx.fillRect(0, 0, MINIMAP_W, MINIMAP_H)
    ctx.globalAlpha = 1
    const t = this.minimapTransform()
    if (!t) return
    for (const node of this.layout.values()) {
      ctx.fillStyle = node.id === this.selectedId ? this.theme.text : `${this.colourOf(node)}80`
      ctx.fillRect(node.x * t.scale + t.offsetX, node.y * t.scale + t.offsetY, Math.max(3, node.width * t.scale), Math.max(2, node.height * t.scale))
    }
    const tl = this.screenToWorld(0, 0)
    const br = this.screenToWorld(this.width, this.height)
    ctx.strokeStyle = this.theme.muted
    ctx.lineWidth = 1
    ctx.strokeRect(tl.x * t.scale + t.offsetX, tl.y * t.scale + t.offsetY, (br.x - tl.x) * t.scale, (br.y - tl.y) * t.scale)
  }

  /** Clicking the minimap centres the view there. */
  private onMinimapClick(event: MouseEvent): void {
    const t = this.minimapTransform()
    if (!t || !this.minimap) return
    const rect = this.minimap.getBoundingClientRect()
    const wx = (event.clientX - rect.left - t.offsetX) / t.scale
    const wy = (event.clientY - rect.top - t.offsetY) / t.scale
    this.cam = { ...this.cam, x: -wx, y: -wy }
    this.render()
  }

  private hitNode(root: LNode, wx: number, wy: number, excludeId: string | null = null): Hit | null {
    if (root.id === excludeId) return null
    const node = this.layout.get(root.id)
    if (node) {
      if (wx >= node.x && wx <= node.x + node.width && wy >= node.y && wy <= node.y + node.height) {
        return { kind: 'node', node: root }
      }
      if (root.children.length > 0) {
        const bx = node.x + node.width + 2
        const by = node.y + node.height / 2
        const br = NODE.collapseButtonRadius / this.cam.zoom + 4
        if (Math.hypot(wx - bx, wy - by) <= br) return { kind: 'collapse', node: root }
      }
    }
    if (!root.collapsed) {
      for (let i = root.children.length - 1; i >= 0; i -= 1) {
        const hit = this.hitNode(root.children[i]!, wx, wy, excludeId)
        if (hit) return hit
      }
    }
    return null
  }

  private hitCheckbox(wx: number, wy: number): LNode | null {
    for (const node of this.layout.values()) {
      const cbX = node.x + (node.depth === 0 ? NODE.rootPadX : NODE.padX)
      const cbY = node.y + node.height / 2 - NODE.checkboxSize / 2
      if (wx >= cbX - 3 && wx <= cbX + NODE.checkboxSize + 3 && wy >= cbY - 3 && wy <= cbY + NODE.checkboxSize + 3) return node
    }
    return null
  }

  private hitLinkBadge(sx: number, sy: number): LNode | null {
    for (const node of this.layout.values()) {
      const badge = this.linkBadgeRect(node)
      if (badge && sx >= badge.x && sx <= badge.x + badge.w && sy >= badge.y && sy <= badge.y + badge.h) return node
    }
    return null
  }

  private local(event: MouseEvent): { x: number; y: number } {
    const rect = this.canvas.getBoundingClientRect()
    return { x: event.clientX - rect.left, y: event.clientY - rect.top }
  }

  private onMouseDown(event: MouseEvent): void {
    if (event.button === 2 || !this.root) return
    this.canvas.focus()
    const p = this.local(event)
    const world = this.screenToWorld(p.x, p.y)

    const badge = this.hitLinkBadge(p.x, p.y)
    if (badge) {
      this.select(badge.id)
      this.callbacks.onLinks(badge.id, event.clientX, event.clientY)
      return
    }

    const checkbox = this.readOnly ? null : this.hitCheckbox(world.x, world.y)
    if (checkbox) {
      this.snapshot()
      checkbox.status = checkbox.status === 'done' ? null : 'done'
      checkbox.updatedAt = nowIso()
      this.select(checkbox.id)
      this.commit()
      return
    }

    const hit = this.hitNode(this.root, world.x, world.y)
    if (hit?.kind === 'collapse') {
      this.toggleCollapse(hit.node.id)
      return
    }
    if (hit) {
      this.select(hit.node.id)
      if (this.readOnly) return
      Object.assign(this.drag, { pending: true, active: false, node: hit.node, startX: event.clientX, startY: event.clientY, dropTargetId: null })
      return
    }

    this.select(null)
    this.panning = true
    this.panStart = { x: event.clientX, y: event.clientY }
    this.camStart = { x: this.cam.x, y: this.cam.y }
    this.canvas.style.cursor = 'grabbing'
  }

  private onMouseMove(event: MouseEvent): void {
    if (!this.root) return
    const drag = this.drag
    if (drag.active && drag.node) {
      drag.node.offsetX = drag.origOffsetX + (event.clientX - drag.startX) / this.cam.zoom
      drag.node.offsetY = drag.origOffsetY + (event.clientY - drag.startY) / this.cam.zoom
      const p = this.local(event)
      const world = this.screenToWorld(p.x, p.y)
      const hit = this.hitNode(this.root, world.x, world.y, drag.node.id)
      let target: string | null = null
      if (hit?.kind === 'node') {
        const draggedId = drag.node.id
        const insideDragged = findMindmapNode(drag.node as unknown as MindmapNode, hit.node.id) !== null
        if (hit.node.id !== draggedId && !insideDragged && this.parentOf(draggedId)?.id !== hit.node.id) target = hit.node.id
      }
      drag.dropTargetId = target
      this.canvas.style.cursor = target ? 'copy' : 'grabbing'
      this.render()
      return
    }
    if (drag.pending && drag.node) {
      if (Math.hypot(event.clientX - drag.startX, event.clientY - drag.startY) >= DRAG_THRESHOLD) {
        Object.assign(drag, {
          active: true,
          pending: false,
          origOffsetX: drag.node.offsetX,
          origOffsetY: drag.node.offsetY,
          before: JSON.stringify(toNode(this.root)),
        })
        this.canvas.style.cursor = 'grabbing'
        this.render()
      }
      return
    }
    if (this.panning) {
      this.cam.x = this.camStart.x + (event.clientX - this.panStart.x) / this.cam.zoom
      this.cam.y = this.camStart.y + (event.clientY - this.panStart.y) / this.cam.zoom
      this.render()
      return
    }
    const p = this.local(event)
    const world = this.screenToWorld(p.x, p.y)
    const hit = this.hitNode(this.root, world.x, world.y)
    const hovered = hit?.kind === 'node' ? hit.node.id : null
    this.canvas.style.cursor = hit || this.hitLinkBadge(p.x, p.y) ? 'pointer' : 'grab'
    if (hovered !== this.hoveredId) {
      this.hoveredId = hovered
      this.render()
    }
  }

  private onMouseUp(): void {
    const drag = this.drag
    if (drag.active && drag.node && this.root) {
      this.snapshot(drag.before)
      const dragged = drag.node
      if (drag.dropTargetId) {
        const target = this.find(drag.dropTargetId)
        const oldParent = this.parentOf(dragged.id)
        if (target && oldParent) {
          dragged.offsetX = 0
          dragged.offsetY = 0
          oldParent.children = oldParent.children.filter((child) => child.id !== dragged.id)
          target.children.push(dragged)
          target.collapsed = false
          dragged.colour = this.colourOf(target)
          this.callbacks.onToast(`Moved "${dragged.title}" → "${target.title}"`)
        }
      }
      Object.assign(drag, { active: false, pending: false, node: null, dropTargetId: null })
      this.canvas.style.cursor = 'grab'
      this.select(dragged.id)
      this.commit()
      return
    }
    if (drag.pending) Object.assign(drag, { pending: false, node: null })
    this.panning = false
    this.canvas.style.cursor = 'grab'
  }

  private onMouseLeave(): void {
    const drag = this.drag
    if (drag.active || drag.pending) {
      if (drag.active && drag.node) {
        drag.node.offsetX = drag.origOffsetX
        drag.node.offsetY = drag.origOffsetY
      }
      Object.assign(drag, { active: false, pending: false, node: null, dropTargetId: null })
      this.render()
    }
    this.panning = false
  }

  private onDoubleClick(event: MouseEvent): void {
    if (!this.root || this.readOnly) return
    const p = this.local(event)
    const world = this.screenToWorld(p.x, p.y)
    if (this.hitCheckbox(world.x, world.y)) return
    const hit = this.hitNode(this.root, world.x, world.y)
    if (hit?.kind === 'node') this.callbacks.onEditText(hit.node.id)
  }

  private onWheel(event: WheelEvent): void {
    event.preventDefault()
    const p = this.local(event)
    const zoom = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, this.cam.zoom * (event.deltaY > 0 ? 0.92 : 1.08)))
    const wx = (p.x - this.width / 2) / this.cam.zoom - this.cam.x
    const wy = (p.y - this.height / 2) / this.cam.zoom - this.cam.y
    this.cam.zoom = zoom
    this.cam.x = (p.x - this.width / 2) / zoom - wx
    this.cam.y = (p.y - this.height / 2) / zoom - wy
    this.callbacks.onView(zoom)
    this.render()
  }

  private onContextMenu(event: MouseEvent): void {
    event.preventDefault()
    if (!this.root) return
    const p = this.local(event)
    const world = this.screenToWorld(p.x, p.y)
    const hit = this.hitNode(this.root, world.x, world.y)
    if (hit?.kind === 'node') {
      this.select(hit.node.id)
      this.callbacks.onContextMenu(hit.node.id, event.clientX, event.clientY)
    } else {
      this.callbacks.onContextMenu(null, event.clientX, event.clientY)
    }
  }
}

interface Rect {
  x: number
  y: number
  w: number
  h: number
}

interface Anchor {
  x: number
  y: number
  dx: number
  dy: number
}

type Hit = { kind: 'node'; node: LNode } | { kind: 'collapse'; node: LNode }
